import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import Select from "react-select";
import { nanoid } from "nanoid";
import { productHandler } from "../../../../redux/handlers";
import { SuccessModal } from "./modals";


export function OptionProductSet() {
  const { productID, businessSlug } = useParams();
  const dispatch = useDispatch();

  const [productset, setProductset] = useState([]);
  const [optionsProduct, setOptionsProduct] = useState([]);
  const [formdisabled, setFormdisabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  // โหลด product list สำหรับ dropdown และ bundle items ที่มีอยู่
  useEffect(() => {
    if (!productID || !businessSlug) return;

    const fetchInitialData = async () => {
      // โหลด product list สำหรับ dropdown
      const productListResponse = await dispatch(
        productHandler.getProductList({
          businessSlug,
          page: 1,
          per_page: 1000,
          s: "",
          sortBy: "name",
          sortOrder: "asc",
          productStatus: "active",
        }),
      );

      if (productListResponse?.data) {
        const options = productListResponse.data.map((p) => ({
          value: p.id,
          label: `${p.sku} - ${p.name}`,
          productPrice: Number(p.price) || 0,
          productName: p.name,
        }));
        setOptionsProduct(options);
      }

      // โหลด bundle items ที่มีอยู่
      const productResponse = await dispatch(
        productHandler.getProductById(productID),
      );

      if (productResponse?.productBundleItemsAsBundle?.length > 0) {
        const existingItems = productResponse.productBundleItemsAsBundle.map(
          (item) => ({
            id: item.id,
            localId: nanoid(),
            productID: item.component_product_id,
            productName: item.componentProduct?.name || "",
            productNum: item.quantity,
            productPrice: Number(item.componentProduct?.price) || 0,
            unit_price:
              item.unit_price != null ? Number(item.unit_price) : null,
          }),
        );
        setProductset(existingItems);
        setFormdisabled(true);
      } else {
        // ไม่มีสินค้าในชุด → เปิด edit mode ทันที
        setFormdisabled(false);
      }
    };

    fetchInitialData();
  }, [productID, businessSlug, dispatch]);

  const handleSave = async () => {
    // validate ทุก row ต้องเลือกสินค้า
    const errors = productset.map((item) => !item.productID);
    setValidationErrors(errors);
    if (errors.some(Boolean)) return;

    setIsSaving(true);
    try {
      const bundleItems = productset.map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        component_product_id: item.productID,
        quantity: Number(item.productNum) || 1,
        unit_price:
          item.unit_price != null ? Number(item.unit_price) : undefined,
      }));

      const response = await dispatch(
        productHandler.updateBundleItems(productID, bundleItems),
      );

      if (response) {
        // sync local state กับ response จาก server
        const savedItems = response.map((item) => ({
          id: item.id,
          localId: nanoid(),
          productID: item.component_product_id,
          productName: item.componentProduct?.name || "",
          productNum: item.quantity,
          productPrice: Number(item.componentProduct?.price) || 0,
          unit_price: item.unit_price != null ? Number(item.unit_price) : null,
        }));
        setProductset(savedItems);
        setValidationErrors([]);
        setSuccessMessage("บันทึกตัวเลือกสินค้าจัดชุดสำเร็จ");
        setShowSuccessModal(true);
        setFormdisabled(true);
      }
    } catch (error) {
      console.error("Error saving bundle items:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card card-st-3 mb-3">
      <div className="card-body px-xl-3">
        <div className="pt-2 pb-1 px-3">
          <span className="fw-medium" style={{ color: "#495057" }}>
            ตัวเลือกสินค้าจัดชุด
          </span>
          <span className="ms-2" style={{ color: "#858796" }}>
            (เช่น ชุดอาหารกลางวัน Combo ข้าว+น้ำ)
          </span>
        </div>

        <OptionProductSetEdit
          productset={productset}
          setProductset={setProductset}
          optionsProduct={optionsProduct}
          formdisabled={formdisabled}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
        />

        <div className="text-end pb-4 pe-3 mt-2">
          {formdisabled ? (
            <Button
              type="button"
              className="btn-st-3 mr-3 ml-auto"
              onClick={() => setFormdisabled(false)}
            >
              แก้ไข
            </Button>
          ) : (
            <div className="d-flex justify-content-end gap-2">
              <Button
                type="button"
                className="btn-st-6 btn"
                onClick={() => { setFormdisabled(true); setValidationErrors([]); }}
                disabled={isSaving}
              >
                ยกเลิก
              </Button>
              <Button
                type="button"
                className="btn-st-3"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "กำลังบันทึก..." : "บันทึก"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <SuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        textsuccess={successMessage}
      />
    </div>
  );
}

function OptionProductSetEdit({
  productset,
  setProductset,
  optionsProduct,
  formdisabled,
  validationErrors = [],
  setValidationErrors,
}) {
  const addHandler = () => {
    setProductset((prev) => [
      ...prev,
      {
        id: null,
        localId: nanoid(),
        productID: "",
        productName: "",
        productNum: 1,
        productPrice: 0,
        unit_price: null,
      },
    ]);
  };

  const handleDelete = (localId) => {
    setProductset((prev) => prev.filter((item) => item.localId !== localId));
  };

  const handleProductChange = (selectedOption, localId, index) => {
    setProductset((prev) =>
      prev.map((el) =>
        el.localId === localId
          ? {
              ...el,
              productID: selectedOption?.value || "",
              productName: selectedOption?.productName || "",
              productPrice: selectedOption?.productPrice || 0,
              productNum: el.productNum >= 1 ? el.productNum : 1,
            }
          : el,
      ),
    );
    // ล้าง error ของ row นี้ทันทีที่เลือก
    if (setValidationErrors) {
      setValidationErrors((prev) => {
        const next = [...prev];
        next[index] = false;
        return next;
      });
    }
  };

  const handleNumChange = (localId, value) => {
    setProductset((prev) =>
      prev.map((el) =>
        el.localId === localId ? { ...el, productNum: value } : el,
      ),
    );
  };

  const sumNum =
    productset.length > 0
      ? productset.reduce((acc, pd) => acc + (parseInt(pd.productNum) || 0), 0)
      : 0;

  const sumPrice =
    productset.length > 0
      ? productset.reduce(
          (acc, pd) =>
            acc +
            (Number(pd.productPrice) || 0) * (parseInt(pd.productNum) || 0),
          0,
        )
      : 0;

  return (
    <div className="px-5 py-4">
      <fieldset disabled={formdisabled}>
        <div className="mt-2 ps-2">
          {productset.map((item, index) => (
            <div key={item.localId}>
              <div className="row gx-4 align-items-center">
                <div className="col">
                  <Form.Group className="mb-2 form-st-3 pb-1">
                    <Form.Label className="mb-1">
                      ชื่อสินค้า <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      name="product"
                      className="w-100 select-st-1"
                      options={optionsProduct}
                      placeholder="เลือกสินค้า"
                      onChange={(e) => handleProductChange(e, item.localId, index)}
                      value={
                        optionsProduct.find(
                          (i) => i.value === item.productID,
                        ) || null
                      }
                      styles={
                        validationErrors[index]
                          ? {
                              control: (base) => ({
                                ...base,
                                borderColor: "#dc3545",
                                "&:hover": { borderColor: "#dc3545" },
                              }),
                            }
                          : undefined
                      }
                    />
                    {validationErrors[index] && (
                      <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                        กรุณาเลือกสินค้า
                      </div>
                    )}
                  </Form.Group>
                </div>
                <div className="col-auto">
                  <Form.Group
                    className="mb-2 form-st-3 pb-1"
                    style={{ width: "150px" }}
                  >
                    <Form.Label className="mb-1">จำนวน</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      className="form-control"
                      placeholder="จำนวน"
                      value={item.productNum}
                      onChange={(e) =>
                        handleNumChange(item.localId, e.target.value)
                      }
                    />
                  </Form.Group>
                </div>
                <div className="col-auto">
                  <Form.Group
                    className="mb-2 form-st-3 pb-1"
                    style={{ width: "150px" }}
                  >
                    <Form.Label className="mb-1">ราคาขาย</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-control"
                      placeholder="ราคาขาย"
                      value={Number(item.productPrice).toFixed(2)}
                      disabled
                    />
                  </Form.Group>
                </div>
                <div className="col-auto">
                  <Form.Group
                    className="mb-2 form-st-3 pb-1"
                    style={{ width: "150px" }}
                  >
                    <Form.Label className="mb-1">ราคารวม</Form.Label>
                    <Form.Control
                      type="text"
                      className="form-control"
                      placeholder="ราคารวม"
                      value={(
                        (Number(item.productPrice) || 0) *
                        (parseInt(item.productNum) || 0)
                      ).toFixed(2)}
                      disabled
                    />
                  </Form.Group>
                </div>
                <div className="col-auto">
                  <div style={{ width: "50px" }}>
                    {index > 0 && (
                      <button
                        type="button"
                        className="btn btn-delete-st-1"
                        onClick={() => handleDelete(item.localId)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="row gx-4 align-items-center">
            <div className="col">
              <button
                type="button"
                className="btn btn-st-3 mb-4"
                onClick={addHandler}
              >
                เพิ่มสินค้าจัดชุด
              </button>
            </div>
            <div className="col-auto" style={{ width: "150px" }}>
              {/* spacer - product column */}
            </div>
            <div className="col-auto">
              <Form.Group
                className="mb-2 form-st-3 pb-1"
                style={{ width: "150px" }}
              >
                <Form.Label className="mb-1">จำนวนรวม</Form.Label>
                <Form.Control
                  type="text"
                  className="form-control"
                  value={sumNum}
                  disabled
                />
              </Form.Group>
            </div>
            <div className="col-auto">
              <div style={{ width: "150px" }} />
            </div>
            <div className="col-auto">
              <Form.Group
                className="mb-2 form-st-3 pb-1"
                style={{ width: "150px" }}
              >
                <Form.Label className="mb-1">ราคารวมทั้งหมด</Form.Label>
                <Form.Control
                  type="text"
                  className="form-control"
                  value={sumPrice.toFixed(2)}
                  disabled
                />
              </Form.Group>
            </div>
            <div className="col-auto">
              <div style={{ width: "50px" }} />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
